import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "../api/index.js";
 
const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    // Check if token and email are present
    if (!token || !email) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token, email]);
 
  // Password validation rules
  const validations = [
    { test: /.{8,}/, label: "At least 8 characters" },
    { test: /[!@#$%^&*(),.?":{}|<>]/, label: "1 special character" },
    { test: /\d/, label: "1 digit" },
    { test: /[A-Z]/, label: "1 uppercase letter" },
  ];
 
  const checkValidations = (password) =>
    validations.map((rule) => rule.test.test(password));
 
  const newPasswordChecks = checkValidations(newPassword);
  const confirmPasswordChecks =
    confirmPassword && confirmPassword === newPassword;
 
  const handleSubmit = async () => {
    if (!newPasswordChecks.every(Boolean)) {
      setError("Password does not meet all requirements");
      return;
    }

    if (!confirmPasswordChecks) {
      setError("Passwords do not match");
      return;
    }

    if (!token || !email) {
      setError("Invalid reset link");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_BASE}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          email,
          newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        // Navigate to login after showing success message
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(data.message || "Failed to reset password");
      }

    } catch (error) {
      console.error("Reset password error:", error);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <Box
      sx={{
        minHeight: "100vh",  // ensures full height
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: "url('/assets/shape.png'), url('/assets/MainBg.png')",
        backgroundSize: "cover, cover",
        backgroundPosition: "center, center",
        px: 2,
        overflowX: "hidden", // only block horizontal scrollbar
        overflowY: "auto",   // vertical auto adjust
      }}
    >
      <Paper
        elevation={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px",
          px: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 4 },
          pb: 0,
          width: { xs: "100%", sm: "450px", md: "500px", lg: "520px" },
          maxHeight: "100vh", // prevents overflow in small screens
          overflowY: "auto", // allow scrolling inside only if needed
        }}
      >
        {!isSubmitted ? (
          // --- Password Reset Form ---
          <Box sx={{ mt: { xs: 3, sm: 4, md: "0rem" } }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontSize: "clamp(1.25rem, 2vw, 1.75rem)",
                mb: "1.5rem",
                textAlign: "center",
                fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',
              }}
            >
              Set New Password
            </Typography>
 
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 4,
                fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
                textAlign: "center",
                lineHeight: 1.5,
                px: { xs: 1, sm: 2 },
                fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',
              }}
            >
              Must be at least 8 characters, contain at least 1 special
              character, and include at least 1 digit.
            </Typography>

            {/* Error Message */}
            {error && (
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontSize: "0.9rem",
                }}
              >
                {error}
              </Typography>
            )}
 
            <Typography
              variant="body2"
              fontWeight="500"
              align="left"
              sx={{ mb: 1 , fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',}}
            >
              New Password
            </Typography>
            <TextField
              type={showNewPassword ? "text" : "password"}
              fullWidth
              variant="outlined"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (error) setError("");
              }}
              sx={{ mb: 1, fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif', }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
 
            {/* Validation bars */}
            <Box sx={{ display: "flex", gap: 1, mb: 3, width: "11rem" , fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',}}>
              {newPasswordChecks.map((passed, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    height: "7px",
                    borderRadius: "2px",
                    backgroundColor: passed ? "limegreen" : "#ff2e76",
                  }}
                />
              ))}
            </Box>
 
            {/* Confirm Password */}
            <Typography
              variant="body2"
              fontWeight="500"
              align="left"
              sx={{ mb: 1 , fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',}}
            >
              Confirm Password
            </Typography>
            <TextField
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (error) setError("");
              }}
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
 
            {/* Confirm bars */}
            <Box sx={{ display: "flex", gap: 1, mb: 4, width: "11rem" }}>
              {Array(validations.length)
                .fill(0)
                .map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',
                      height: "8px",
                      borderRadius: "2px",
                      backgroundColor: confirmPassword
                        ? confirmPasswordChecks
                          ? "limegreen"
                          : "#ff2e76"
                        : "#ddd",
                    }}
                  />
                ))}
            </Box>
 
            <Button
              fullWidth
              variant="contained"
              disabled={loading || !token || !email}
              sx={{
                background: "linear-gradient(to right, #4f8cff, #567dff)",
                textTransform: "none",
                fontSize: "16px",
                textDecoration: "underline",
                margin: "3rem 0",
                py: 1.2,
                fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif',
              }}
              onClick={handleSubmit}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              py: 6,
              pt: "4rem",
              pb: "18rem",
              minHeight: "78vh",  
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <img
              src="/assets/alldone.png"
              alt="Success"
              style={{
                width: "40px",
                height: "40px",
                objectFit: "contain",
              }}
            />
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ mb: 1 , fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif'}}
            >
              Password Reset Successful!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{fontFamily:'"Nunito Sans", "Roboto", "Arial", sans-serif', textAlign: "center"}}
            >
              Your password has been reset successfully.<br/>
              Redirecting to login page...
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
 
export default ResetPassword;